import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { saveLoginInfo } from "../../../../features/mobile/commonSlice";
import { regMobile } from "../../components/logic";
import api from "../../../../common/api";
import HeadCont from "../../components/common/HeadCont";
import InputBox from "../../components/common/InputBox";
import TitleBox from "../../components/common/TitleBox";
import Btn from "../../components/common/Btn";
import Dialog from "../../components/common/Dialog";
import { storage } from "../../../../utils/storage";
import firebaseMessaging from "../../../../utils/firebaseMessaging";

const SignIn = () => {
    const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loginInfo } = useSelector((state) => state.commonApp);
  const [loginDatas, setLoginDatas] = useState({ email: "", pass: "", token: "" });
  const [active1, setActive1] = useState("default");
  const [active2, setActive2] = useState("default");
  const [errorMsg, setErrorMsg] = useState({ msg1: "", msg2: "" });
  const [openDialog, setOpenDialog] = useState(false);
  const [autoLogin, setAutoLogin] = useState(true); // Auto-login enabled by default
  const emailRef = useRef(null);
  const passRef = useRef(null);

  const onChangeInput1 = (e) => {
    setLoginDatas({ ...loginDatas, email: e.target.value });

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(e.target.value)) {
      setActive1("on");
      setErrorMsg({ ...errorMsg, msg1: "" });
    } else if (e.target.value.length > 0) {
      setActive1("ready");
    } else {
      setActive1("default");
    }
  };

  const onChangeInput2 = (e) => {
    setLoginDatas({ ...loginDatas, pass: e.target.value });

    // Clear error message when user starts typing
    if (errorMsg.msg2) {
      setErrorMsg({ ...errorMsg, msg2: "" });
    }

    if (e.target.value?.length >= 1) setActive2("on");
    // else setActive2("ready");
  };

  const onDeleteValue = (type) => {
    if (type === "email") {
      setLoginDatas({ ...loginDatas, email: "" });
      setErrorMsg({ ...errorMsg, msg1: "" });
      setActive1("default");
      emailRef.current?.focus();
    } else {
      setLoginDatas({ ...loginDatas, pass: "" });
      setErrorMsg({ ...errorMsg, msg2: "" });
      setActive2("default");
      passRef.current?.focus();
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    const sessionToken = window.sessionStorage.getItem("token");

    // Temporarily allow login attempt even without full validation for debugging
    try {
      let params = {
        email: loginDatas.email,
        password: loginDatas.pass,
        firebaseToken: sessionToken,
      };

      console.log('Attempting login with:', { email: params.email, hasPassword: !!params.password });
      const res = await api.post(`/auth/email/login`, params);
      console.log('Login response:', res);

      if (res.data.ok) {
          // Store JWT token - critical for authentication
          try {
            await storage.setItem('accessToken', res.data.accessToken);
          } catch (storageError) {
            console.error('Failed to store access token:', storageError);
            // Continue anyway - token is still in memory via Redux
          }

          // Save auto-login credentials if enabled (non-critical)
          try {
            if (autoLogin) {
              await storage.setItem('autoLogin', 'true');
              await storage.setItem('autoLoginEmail', loginDatas.email);
              await storage.setItem('autoLoginPass', loginDatas.pass);
            } else {
              // Clear auto-login credentials if disabled
              await storage.removeItem('autoLogin');
              await storage.removeItem('autoLoginEmail');
              await storage.removeItem('autoLoginPass');
            }
          } catch (storageError) {
            console.error('Failed to save auto-login credentials:', storageError);
            // Non-critical, continue login flow
          }

          // Check if registration is incomplete
          if (res.data.registrationStep && res.data.registrationStep !== 'completed') {
            // Store user data in sessionStorage to continue signup
            try {
              sessionStorage.setItem('incompleteRegistration', JSON.stringify({
                email: loginDatas.email,
                userId: res.data.userId,
                nickName: res.data.nickName,
                registrationStep: res.data.registrationStep
              }));
            } catch (storageError) {
              console.error('Failed to store incomplete registration data:', storageError);
            }

            // Redirect to signup page to continue from where they left off
            // registrationStep values:
            // 'email_verified' → step 2 (password setup)
            // 'terms_agreed' → step 5 (nickname/character)
            let resumeStep = 5; // default to nickname step
            if (res.data.registrationStep === 'email_verified') {
              resumeStep = 2; // password setup step
            }

            navigate('/emailsignup', {
              state: {
                continueRegistration: true,
                email: loginDatas.email,
                userId: res.data.userId,
                step: resumeStep
              }
            });
            return;
          }

          // Save login info to Redux first (most critical)
          dispatch(saveLoginInfo({ ...res.data, existPass: true }));

          // Save userId and token to localStorage for Firebase Messaging
          try {
            localStorage.setItem('accessToken', res.data.accessToken);
            localStorage.setItem('userId', res.data.userId);

            // Send FCM token to backend now that user is logged in
            const fcmToken = firebaseMessaging.getToken();
            if (fcmToken) {
              console.log('Sending FCM token to backend after login...');
              await firebaseMessaging.sendTokenToBackend(fcmToken);
            }

            // Send user's timezone to backend
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            console.log('User timezone:', userTimezone);
            await api.put(`/user/timezone`, {
              userId: res.data.userId,
              timezone: userTimezone
            }, {
              headers: { authorization: `Bearer ${res.data.accessToken}` }
            });
          } catch (err) {
            console.error('Error saving user data:', err);
            // Non-critical, continue login flow
          }

          // Show success dialog
          setOpenDialog(true);

          // Optional: hide overflow on main element (cosmetic)
          try {
            const main = document.querySelector(".main");
            if (main) {
              main.style.overflow = "hidden";
              main.style.width = "100%";
            }
          } catch (err) {
            console.error('Error setting main element styles:', err);
          }

          // Clear form fields
          setLoginDatas({ email: "", pass: "", token: "" });
          if (emailRef.current) emailRef.current.value = "";
          if (passRef.current) passRef.current.value = "";
        } else {
          const error = res.data.error;
          setErrorMsg({ ...errorMsg, msg2: error });

          // If error is about email (not registered), clear email field
          if (error.includes('email is not registered') || error.includes('email is registered using')) {
            setLoginDatas({ email: "", pass: "", token: "" });
            setActive1("default");
            setActive2("default");
            if (emailRef.current) {
              emailRef.current.value = "";
              emailRef.current.focus();
            }
            if (passRef.current) passRef.current.value = "";
          }
          // If error is about password, keep email and only clear password
          else {
            setLoginDatas({ ...loginDatas, pass: "", token: "" });
            setActive2("default");
            if (passRef.current) {
              passRef.current.value = "";
              passRef.current.focus();
            }
          }
        }
    } catch (err) {
      console.error('Login error:', err);

      // Check if this is an HTTP error response from the backend
      if (err.response) {
        // Backend responded with an error status
        const backendError = err.response.data?.message || err.response.data?.error || 'Login failed. Please try again.';
        setErrorMsg({ ...errorMsg, msg2: backendError });

        // If error is about email (not registered), clear email field
        if (backendError.includes('email is not registered') || backendError.includes('email is registered using')) {
          setLoginDatas({ email: "", pass: "", token: "" });
          setActive1("default");
          setActive2("default");
          if (emailRef.current) {
            emailRef.current.value = "";
            emailRef.current.focus();
          }
          if (passRef.current) passRef.current.value = "";
        }
        // If error is about password, keep email and only clear password
        else {
          setLoginDatas({ ...loginDatas, pass: "", token: "" });
          setActive2("default");
          if (passRef.current) {
            passRef.current.value = "";
            passRef.current.focus();
          }
        }
      } else {
        // Network error or no response from server
        console.error('Network or connection error:', err.message);
        setErrorMsg({ ...errorMsg, msg2: "Connection error. Please check your network and try again." });
      }
    }
  };

  useEffect(() => {
    let focused = true;
    if (focused) emailRef.current?.focus();
    return () => (focused = false);
  }, [emailRef]);

  useEffect(() => {
    if (loginDatas.pass?.length > 1) {
      setActive2("on");
    } else if (loginDatas.pass?.length === 0) {
      // Don't clear error message here - it will be cleared when user types
      setActive2("default");
    }
    // eslint-disable-next-line
  }, [loginDatas.email, loginDatas.pass]);

  return (
    <>
      {/* <HeadCont rightIcon={"no icon"} /> */}
      <form onSubmit={onLogin} className="mx-6 mt-[80px] flex flex-col gap-6">
        {/* <TitleBox option={2} title={"Login with Phone Number"} /> */}
        {/* 디자인상 3번째, 8번째 자리에 띄어쓰기를 해야하기 때문에 type='number' 대신 'text'로 진행 */}
        <InputBox
          option={"label"}
          placeholder={"Enter your email"}
          inputM={"email"}
          refName={emailRef}
          id={"email"}
          nameTitle={"email"}
          onChangeAction={(e) => onChangeInput1(e)}
          value={loginDatas.email || ""}
          active={active1}
          onClickAction={() => onDeleteValue("email")}
          errorMsg={errorMsg.msg1}
          login={true}
        />
        <InputBox
          option={"passlabel"}
          // label={"Password"}
          placeholder={"Enter password"}
          refName={passRef}
          id={"pass"}
          nameTitle={"pass"}
          length={4}
          onChangeAction={(e) => onChangeInput2(e)}
          value={loginDatas.pass || ""}
          active={active2}
          onClickAction={() => onDeleteValue("pass")}
          errorMsg={errorMsg.msg2}
        />

        {/* Auto-login checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="autoLogin"
            checked={autoLogin}
            onChange={(e) => setAutoLogin(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-main focus:ring-main cursor-pointer"
          />
          <label htmlFor="autoLogin" className="text-sm text-gray_160 cursor-pointer select-none">
            Keep me logged in
          </label>
        </div>

        {/* 디자인대로 로그인 버튼 바로 위에 fixed 위치했다가 갤럭시에서 flot되어 비밀번호 위에 올라가는 현상으로 디자인 변경 */}
        {/* <div className="space-y-2 text-center">
          <p className="text-sm font-light">{"Having trouble logging in?"}</p>
          <ul className="flex justify-center text-lg font-bold text-gray_160">
            <li onClick={() => navigate(`/findpass`)}>{"Find Password"}</li>
            <span className="mx-2 my-1 w-[1px] bg-gray_60"></span>
            <li onClick={() => navigate(`/sign`)}>{"Try another login method"}</li>
          </ul>
        </div> */}
        <Btn
          bgColor={"bg-main"}
          textColor={"text-white font-semibold text-lg"}
          borderColor={"border border-main"}
          title={"Login"}
          onAction={onLogin}
          // active={active1 === "on" && active2 === "on" ? "on" : "ready"}
          active={"on"}
        />
      </form>
      {openDialog && (
        // eslint-disable-next-line no-restricted-globals
        <Dialog
          innerClass={"py-4 pl-6 pr-5"}
          btnClass={"mt-8"}
          title={"Login Successful!"}
          desc={`Welcome back, ${loginInfo?.nickName || 'User'}!`}
          onRightBtnAction={() => navigate(`/home`)}
          onCoverAction={() => navigate(`/home`)}
        />
      )}
    </>
  );
};

export default SignIn;
